// ═══════════════════════════════════════════════════════════════════════
// Contrôle de la carte employé (QR) — écran web du personnel habilité (agents de remise, accueil, gardes) :
//   • lecture du QR par la caméra du navigateur quand l'API native BarcodeDetector la permet (aucune bibliothèque) ;
//     sinon, et toujours, saisie du texte du QR : un lecteur « douchette » tape le texte comme un clavier, puis Entrée ;
//   • POST /api/cartes/controle → résultat en grand (couleur + mot + icône, jamais la couleur seule), photo de contrôle,
//     identité, poste, service, site, statut, et NUMÉRO DU CONTRÔLE (repris par la remise d'une campagne) ;
//   • réservé à la permission controler_cartes_employes ; le serveur reste juge (403 → « pas habilité »).
// Composants définis hors de tout parent (react-component-rules, règle 1).
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from 'react';
import { Camera, CameraOff, ClipboardPaste, Copy, ScanQrCode, ShieldAlert, UserRound } from 'lucide-react';
import Layout from '../../../components/Layout';
import { useAuth } from '../../../context/AuthContext';
import { Bouton, BandeauInfo, EnTetePage, EtatVide, Squelette } from '../../../design/composants';
import { erreurDe } from '../api';
import { ErreurServeur, Fenetre } from '../commun';
import { dateHeureLongue } from '../apiSocial';
import { useCarte, numeroControle, RESULTATS, COULEURS, PERMISSION_CONTROLE, LONGUEUR_QR_MAX } from './apiCarte';

export const MESSAGE_NON_HABILITE = 'Vous n\'êtes pas habilité à contrôler les cartes';

// ─── Caméra (API native, facultative) ─────────────────────────────────────────────────────────
async function cameraQrDisponible() {
  try {
    if (typeof window === 'undefined' || typeof window.BarcodeDetector !== 'function') return false;
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') return false;
    const formats = typeof window.BarcodeDetector.getSupportedFormats === 'function' ? await window.BarcodeDetector.getSupportedFormats() : [];
    return Array.isArray(formats) && formats.includes('qr_code');
  } catch { return false; }
}

/** true / false une fois la détection faite, null pendant la détection. */
function useCameraQr() {
  const [dispo, setDispo] = useState(null);
  useEffect(() => {
    let vivant = true;
    cameraQrDisponible().then((x) => { if (vivant) setDispo(x); });
    return () => { vivant = false; };
  }, []);
  return dispo;
}

/** Aperçu de la caméra arrière ; le premier QR détecté est transmis une seule fois, puis la caméra s'arrête. */
function LecteurCamera({ onLu, onArret }) {
  const video = useRef(null);
  const rappel = useRef(onLu);
  const [erreur, setErreur] = useState(null);
  rappel.current = onLu;
  useEffect(() => {
    let flux = null;
    let minuteur = null;
    let fini = false;
    const detecteur = new window.BarcodeDetector({ formats: ['qr_code'] });
    const tour = async () => {
      if (fini) return;
      try {
        const codes = await detecteur.detect(video.current);
        const c = (codes || []).find((x) => x && x.rawValue);
        if (c && !fini) { fini = true; rappel.current(c.rawValue); return; }
      } catch { /* image pas encore prête */ }
      minuteur = setTimeout(tour, 200);
    };
    (async () => {
      try {
        flux = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (fini || !video.current) { flux.getTracks().forEach((t) => t.stop()); return; }
        video.current.srcObject = flux;
        await video.current.play();
        tour();
      } catch (e) {
        setErreur(e && e.name === 'NotAllowedError'
          ? 'L\'accès à la caméra a été refusé. Autorisez-le dans le navigateur, ou utilisez le lecteur de QR ou la saisie.'
          : 'La caméra n\'a pas pu être ouverte. Utilisez le lecteur de QR ou la saisie.');
      }
    })();
    return () => { fini = true; clearTimeout(minuteur); if (flux) flux.getTracks().forEach((t) => t.stop()); };
  }, []);
  return (
    <div style={{ margin: '0 0 12px' }}>
      {erreur ? <BandeauInfo ton="alerte" titre="Caméra indisponible">{erreur}</BandeauInfo> : (
        <div style={{ position: 'relative', maxWidth: 420, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
          <video ref={video} muted playsInline aria-label="Aperçu de la caméra : présentez le QR de la carte" style={{ display: 'block', width: '100%', maxHeight: 320, objectFit: 'cover' }} />
          <div aria-hidden="true" style={{ position: 'absolute', inset: '18%', border: '3px solid rgba(255,255,255,0.85)', borderRadius: 12 }} />
        </div>
      )}
      <div style={{ marginTop: 8 }}><Bouton petit Icone={CameraOff} onClick={onArret}>Arrêter la caméra</Bouton></div>
    </div>
  );
}

// ─── Résultat ─────────────────────────────────────────────────────────────────────────────────
/** Photo de contrôle (route réservée à l'auteur du contrôle, 5 minutes), à défaut un cadre « photo indisponible ». */
function PhotoControle({ chemin, nom }) {
  const api = useCarte();
  const [url, setUrl] = useState(null);
  const [etat, setEtat] = useState(chemin ? 'chargement' : 'absente');
  useEffect(() => {
    if (!chemin) { setEtat('absente'); return undefined; }
    let vivant = true;
    let adresse = null;
    setEtat('chargement');
    api.photo(chemin)
      .then((b) => { if (!vivant) return; adresse = URL.createObjectURL(b); setUrl(adresse); setEtat('ok'); })
      .catch(() => { if (vivant) setEtat('absente'); });
    return () => { vivant = false; if (adresse) URL.revokeObjectURL(adresse); };
  }, [api, chemin]);
  const cadre = { width: 128, height: 160, borderRadius: 8, border: '1px solid var(--sg-bord-fort)', background: 'var(--sg-surface)', flex: '0 0 auto', objectFit: 'cover' };
  if (etat === 'ok' && url) return <img src={url} alt={nom ? `Visage de ${nom}, tel qu'enregistré au dossier` : 'Visage du salarié'} style={cadre} />;
  if (etat === 'chargement') return <div style={cadre}><Squelette hauteur={160} /></div>;
  return (
    <div style={{ ...cadre, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--sg-texte-3)', fontSize: 12, textAlign: 'center', padding: 8 }}>
      <UserRound size={36} aria-hidden="true" />Photo indisponible : vérifiez l'identité par une autre pièce.
    </div>
  );
}

function Identite({ identite }) {
  const nom = [identite.prenom, identite.nom].filter(Boolean).join(' ');
  const ligne = (lib, v) => (
    <div style={{ display: 'flex', gap: 6, fontSize: 15, flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--sg-texte-3)', minWidth: 64 }}>{lib}</span>
      <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{v || <span style={{ color: 'var(--sg-texte-3)', fontWeight: 400 }}>Non renseigné</span>}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', margin: '4px 0 12px' }}>
      <PhotoControle chemin={identite.photo_url_controle} nom={nom} />
      <div style={{ minWidth: 0, flex: '1 1 200px', display: 'grid', gap: 4 }}>
        <div style={{ fontSize: 22, fontWeight: 700, overflowWrap: 'anywhere' }}>{nom}</div>
        {ligne('Matricule', identite.matricule)}
        {ligne('Poste', identite.poste)}
        {ligne('Service', identite.service)}
        {ligne('Site', identite.site)}
      </div>
    </div>
  );
}

function NumeroDuControle({ numero }) {
  const [copie, setCopie] = useState(false);
  if (!numero) {
    return (
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>
        <strong>Numéro du contrôle non communiqué par le serveur.</strong> Pour une remise, le serveur retiendra votre dernier contrôle valide de ce salarié (moins de 15 minutes).
      </p>
    );
  }
  const copier = async () => {
    try { await navigator.clipboard.writeText(numero); setCopie(true); } catch { setCopie(false); }
  };
  return (
    <div style={{ margin: '0 0 12px' }}>
      <div style={{ fontSize: 13, color: 'var(--sg-texte-2)', marginBottom: 2 }}>Numéro du contrôle (à reprendre pour la remise)</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code data-numero-controle={numero} style={{ fontSize: 15, fontWeight: 600, padding: '4px 8px', borderRadius: 6, background: 'var(--sg-surface)', border: '1px solid var(--sg-bord)', wordBreak: 'break-all' }}>{numero}</code>
        <Bouton petit variante="discret" Icone={Copy} onClick={copier}>{copie ? 'Copié' : 'Copier'}</Bouton>
      </div>
    </div>
  );
}

/** Résultat d'un contrôle, en grand : couleur, icône ET mot (jamais la couleur seule). */
export function ResultatControle({ controle, onUtiliser, libelleUtiliser = 'Utiliser ce contrôle' }) {
  const { r, le } = controle;
  const def = RESULTATS[r.resultat] || { ton: 'neutre', Icone: ShieldAlert, mot: 'RÉSULTAT NON RECONNU', texte: 'Le serveur a renvoyé un résultat inattendu : en cas de doute, refusez la carte.' };
  const c = COULEURS[def.ton] || COULEURS.neutre;
  const valide = r.resultat === 'VALIDE';
  const numero = numeroControle(r);
  const { Icone } = def;
  return (
    <section className="rh-carte" role="status" aria-live="assertive" data-resultat={r.resultat}
      style={{ border: `3px solid ${c.bord}`, background: c.fond, margin: '0 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Icone size={44} color={c.texte} aria-hidden="true" style={{ flex: '0 0 auto' }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 26, lineHeight: 1.15, fontWeight: 800, color: c.texte, letterSpacing: 0.3, overflowWrap: 'anywhere' }}>{def.mot}</div>
          <div style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>Contrôle du {dateHeureLongue(le)}</div>
        </div>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 15 }}>{def.texte}</p>
      {valide && r.identite && <Identite identite={r.identite} />}
      {r.statut && (r.statut.libelle || r.statut.code) && <p style={{ margin: '0 0 12px', fontSize: 14 }}>Statut de la carte : <strong>{r.statut.libelle || r.statut.code}</strong></p>}
      {valide && <NumeroDuControle numero={numero} />}
      {valide && onUtiliser && (
        <Bouton variante="primaire" Icone={ScanQrCode} onClick={() => onUtiliser({ numero, identite: r.identite || null, le })}>{libelleUtiliser}</Bouton>
      )}
    </section>
  );
}

// ─── Contrôle (saisie, caméra, appel) ─────────────────────────────────────────────────────────
/** Refus d'habilitation (sans appel quand le droit manque ; après un 403 du serveur sinon). */
export function NonHabilite({ message }) {
  return (
    <EtatVide Icone={ShieldAlert} titre={MESSAGE_NON_HABILITE}
      texte={message || 'Le contrôle des cartes employé demande l\'habilitation « Contrôler la carte employé », attribuée par le service RH.'} />
  );
}

/**
 * Bloc de contrôle complet. `onResultat(controle)` à chaque résultat ; `onUtiliser({ numero, identite, le })` affiche,
 * pour un résultat VALIDE, le bouton qui reprend le contrôle (parcours de remise).
 */
export function ControleCarte({ onResultat, onUtiliser, libelleUtiliser }) {
  const { hasPermission } = useAuth();
  const api = useCarte();
  const camera = useCameraQr();
  const saisie = useRef(null);
  const [qr, setQr] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [controle, setControle] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [refus, setRefus] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const habilite = hasPermission(PERMISSION_CONTROLE);
  const collerPossible = typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.readText === 'function';

  const controler = async (texte) => {
    const t = String(texte || '').trim();
    if (!t || enCours) return;
    if (t.length > LONGUEUR_QR_MAX) { setErreur({ statut: 400, code: 'DONNEES_INVALIDES', message: 'Ce texte est trop long pour être un QR de carte : relisez la carte.', erreurs: [] }); return; }
    setEnCours(true); setErreur(null);
    try {
      const r = await api.controler(t);
      const c = { r: r || {}, le: new Date().toISOString() };
      setControle(c); setQr('');
      if (onResultat) onResultat(c);
    } catch (e) {
      const n = erreurDe(e, 'Contrôle impossible.');
      if (n.statut === 403) setRefus(n); else setErreur(n);
    }
    setEnCours(false);
    if (saisie.current) saisie.current.focus();
  };
  const coller = async () => {
    try { const t = await navigator.clipboard.readText(); setQr(t); if (t && t.trim()) controler(t); } catch { setErreur({ statut: 0, code: null, message: 'Le navigateur n\'autorise pas la lecture du presse-papiers : collez le texte dans le champ (Ctrl+V).', erreurs: [] }); }
  };

  if (!habilite) return <NonHabilite />;
  if (refus) return <NonHabilite message={refus.message} />;
  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); controler(qr); }} style={{ margin: '0 0 12px' }}>
        <label htmlFor="qr-carte" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>QR de la carte (lecteur de QR, ou texte collé)</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input id="qr-carte" ref={saisie} className="rh-champ-saisie" value={qr} onChange={(e) => setQr(e.target.value)} autoFocus autoComplete="off" spellCheck={false}
            maxLength={LONGUEUR_QR_MAX + 64} placeholder="SGC1.…" aria-describedby="qr-carte-aide" style={{ flex: '1 1 220px', minWidth: 0, fontFamily: 'monospace' }} />
          <Bouton type="submit" variante="primaire" Icone={ScanQrCode} disabled={enCours || !qr.trim()}>{enCours ? 'Contrôle…' : 'Contrôler'}</Bouton>
        </div>
        <span id="qr-carte-aide" className="rh-form-aide" style={{ display: 'block' }}>
          Avec un lecteur de QR (douchette), lisez la carte : le texte s'inscrit ici et le contrôle part à la touche Entrée envoyée par le lecteur. Le QR affiché par l'application change toutes les 30 secondes et ne vaut qu'une minute.
        </span>
      </form>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 12px' }}>
        {camera && !cameraActive && <Bouton Icone={Camera} onClick={() => { setErreur(null); setCameraActive(true); }}>Lire avec la caméra</Bouton>}
        {collerPossible && <Bouton Icone={ClipboardPaste} disabled={enCours} onClick={coller}>Coller et contrôler</Bouton>}
      </div>
      {camera === false && (
        <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>
          La lecture par la caméra n'est pas proposée par ce navigateur : utilisez un lecteur de QR ou collez le texte du QR.
        </p>
      )}
      {cameraActive && <LecteurCamera onLu={(t) => { setCameraActive(false); setQr(t); controler(t); }} onArret={() => setCameraActive(false)} />}
      <ErreurServeur erreur={erreur} />
      {controle && <ResultatControle controle={controle} onUtiliser={onUtiliser} libelleUtiliser={libelleUtiliser} />}
    </div>
  );
}

/** Fenêtre de contrôle ouverte depuis un parcours (remise) : reprend le numéro d'un contrôle VALIDE. */
export function FenetreControleCarte({ onFermer, onUtiliser }) {
  return (
    <Fenetre titre="Contrôler la carte du salarié" Icone={ScanQrCode} onFermer={onFermer} large>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        Faites afficher sa carte au salarié dans l'application SOGAS Employé, lisez le QR, puis vérifiez que la photo correspond à la personne présente.
      </p>
      <ControleCarte onUtiliser={onUtiliser} libelleUtiliser="Utiliser ce contrôle pour la remise" />
    </Fenetre>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────────────────────
function DerniersControles({ lignes }) {
  if (!lignes.length) return null;
  return (
    <section className="rh-carte">
      <h2 className="rh-form-section-titre" style={{ marginTop: 0 }}>Contrôles de cette session</h2>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {lignes.map((c) => {
          const def = RESULTATS[c.r.resultat];
          const nom = c.r.identite ? [c.r.identite.prenom, c.r.identite.nom].filter(Boolean).join(' ') : '';
          const numero = numeroControle(c.r);
          return (
            <li key={c.le} style={{ padding: '8px 0', borderBottom: '1px solid var(--sg-bord)', fontSize: 14, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <span><strong>{def ? def.mot.toLowerCase().replace(/^./, (x) => x.toUpperCase()) : c.r.resultat}</strong>{nom ? ` — ${nom} (${c.r.identite.matricule || 'sans matricule'})` : ''}</span>
              <span style={{ color: 'var(--sg-texte-3)' }}>{dateHeureLongue(c.le)}{numero ? ` · n° ${numero}` : ''}</span>
            </li>
          );
        })}
      </ul>
      <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>Liste propre à cet onglet, effacée à sa fermeture. Le journal officiel des contrôles est tenu par le serveur.</p>
    </section>
  );
}

export default function ControleCartePage() {
  const [historique, setHistorique] = useState([]);
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Dispositifs sociaux', vers: '/dispositifs' }, { libelle: 'Contrôle des cartes' }]} titre="Contrôler une carte employé"
          sousTitre="Lecture du QR de la carte affichée dans l'application SOGAS Employé. Chaque contrôle est enregistré et tracé." />
        <section className="rh-carte">
          <ControleCarte onResultat={(c) => setHistorique((h) => [c, ...h].slice(0, 10))} />
        </section>
        <DerniersControles lignes={historique} />
      </div>
    </Layout>
  );
}
